import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Collapse,
  IconButton,
  InputBase,
  Skeleton,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useUntypedTranslation } from '../../../hooks/useUntypedTranslation';
import type { SupervisorProposalState } from '../../../hooks/advisory/useSupervisorTurn';
import {
  eyebrow,
  focusRing,
  heading,
  keyQuestionCard,
  keyQuestionRowGrid,
  meta,
  mono,
  pillAction,
  pressable,
  removeControl,
  subQuestionRowGrid,
} from '../../../styles/visualLanguage';
import { normalizeSingleLine } from '../../../redux/slices/advisory/advisoryTextSanitize';
import { AdvisoryEngagementShell } from './AdvisoryEngagementShell';
import { AdvisoryCalloutState } from './AdvisoryCalloutState';
import { stepPosition } from '../advisoryStepPosition';
import {
  KEY_QUESTION_MIN,
  canConfirmKeyQuestions,
  canRemoveKeyQuestion,
} from '../advisoryPresentation';
import { mapAdvisoryErrorCode } from '../../../redux/slices/advisory/advisoryErrorCodes';
import { compareKeyQuestionOrder } from '../../../redux/slices/advisory/advisoryTypes';
import type { ChannelState, KeyQuestionDoc } from '../../../redux/slices/advisory/advisoryTypes';

/**
 * AdvisoryKeyQuestionEditor
 *
 * The key-questions step body — cards separated by a hairline, each with
 * an accent-deep `KQ n` marker, its question and its indented sub-question
 * list; the remove path for a whole key question, through a guarded
 * optimistic thunk; the live confirmed count; the floor-of-one gate with
 * deliberately no ceiling; the two purpose-written empty states; and the
 * step-specific confirm plus Back.
 *
 * Receives already-derived, narrowed data and mints no navigation of its
 * own, mirroring the deleted `AdvisoryCriteriaEditor`'s own precedent. This
 * component renders exactly what the `keyQuestions` prop holds and
 * fabricates nothing — no default questions, no example sub-questions, no
 * client-side proposal seam.
 *
 * This editor is cards, a whole-question remove, an add field, and
 * confirm. The add field sends `addQuestion` through the same supervisor
 * turn every other request uses — the server responds with a recompacted
 * `questions` SSE event carrying the full updated list.
 *
 * Composes exactly ONE `AdvisoryEngagementShell` and adds no chrome of its
 * own — the shell renders in every channel state so the step counter and
 * Leave affordance never disappear; only the body below changes.
 */
export interface AdvisoryKeyQuestionEditorProps {
  projectId: string;
  /** Workspace preview reuses the body without wizard chrome. Defaults preserve the live journey. */
  embedded?: boolean;
  readOnly?: boolean;
  busy?: boolean;
  onPendingChange?: (pending: boolean) => void;
  resolvedAddQuery?: string;
  keyQuestions: KeyQuestionDoc[];
  channelState: ChannelState;
  channelErrorCode: string | null;
  /**
   * The current turn's `questionsRemoved` orphaned map
   * (`useSupervisorTurn.ts`'s own `orphaned` state, passed through
   * unchanged) — keys are ids of questions in `keyQuestions` above that
   * lost a `dependsOn` entry because of the removal the user just
   * performed. `{}` when nothing is orphaned or no removal has completed.
   */
  orphaned: Record<string, string[]>;
  onRemove: (keyQuestion: KeyQuestionDoc) => Promise<string | null>;
  onAdd: (questionText: string) => Promise<string | null>;
  proposals: SupervisorProposalState | null;
  onAccept: (proposalId: string) => Promise<string | null>;
  onRetryChannel: () => void;
  onBack: () => void;
  onConfirm: () => void;
}

export function AdvisoryKeyQuestionEditor({
  embedded = false,
  readOnly = false,
  busy = false,
  onPendingChange,
  resolvedAddQuery,
  keyQuestions,
  channelState,
  channelErrorCode,
  orphaned,
  onRemove,
  onAdd,
  proposals,
  onAccept,
  onRetryChannel,
  onBack,
  onConfirm,
}: AdvisoryKeyQuestionEditorProps) {
  const { t } = useUntypedTranslation();
  const [removeErrorI18nKey, setRemoveErrorI18nKey] = useState<string | null>(null);
  // Server state cannot distinguish "never had any" from "you removed them
  // all" — both are `length === 0` with a healthy channel. This flag is
  // local, ephemeral component state, set true the first time this
  // mount's own remove handler resolves successfully.
  const [keyQuestionsEmptiedByUser, setKeyQuestionsEmptiedByUser] = useState(false);

  // keyQuestions is counted step '03' on the customize path only (was '04'
  // — the customize sequence dropped from five counted steps to four) —
  // always non-null in practice (the page never reaches this component on
  // any other path), the fallback below mirrors the same defensive pattern
  // the now-deleted AdvisoryCriteriaEditor once used.
  const position = stepPosition('keyQuestions', 'customize');

  // Render in the server's `order` sequence, never the `keyQuestions`
  // prop's own array position. `order` is a topological sort of the
  // engagement's dependency graph computed server-side at instantiate time
  // — it is authoritative regardless of which upstream source (a live SSE
  // turn or the Firestore-fed poll/listener path) produced this prop's
  // array order. `compareKeyQuestionOrder` (`advisoryTypes.ts`) is the
  // SAME comparator `advisorySlice.ts`'s merge path uses, so the two can
  // never disagree. `[...keyQuestions]` copies before sorting —
  // `Array#sort` mutates in place, and this prop is caller-owned.
  const orderedKeyQuestions = [...keyQuestions].sort(compareKeyQuestionOrder);

  // Resolve each orphaned id to its OWN question text — never rendered by
  // raw id. The server only reports ids still present after removal
  // (`orphaned`'s keys are never the just-removed id itself), so every key
  // is expected to resolve against `keyQuestions`; one that somehow does
  // not is dropped rather than rendering an empty fragment.
  const orphanedQuestionTexts = Object.keys(orphaned)
    .map(id => keyQuestions.find(q => q.id === id)?.question)
    .filter((text): text is string => Boolean(text));

  const confirmEnabled = canConfirmKeyQuestions(keyQuestions.length);

  // A REMOVAL floor became necessary once the add path above was deleted.
  // Dropping to zero has no recovery until a propose/accept flow is
  // reintroduced; this same rule moves onto the supervisor's
  // `removeQuestions` endpoint once removal is wired through it.
  const removalEnabled = canRemoveKeyQuestion(keyQuestions.length);

  // Mirrors the deleted `AdvisoryCriteriaEditor` counterpart — same three
  // gaps (no mount guard on the post-`await` writes, no `disabled` while a
  // removal is in flight, one shared error slot written by whichever
  // promise settles last), same closure.
  const [removingId, setRemovingId] = useState<string | null>(null);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const [addText, setAddText] = useState('');
  const [addingInFlight, setAddingInFlight] = useState(false);
  useEffect(() => {
    if (resolvedAddQuery)
      setAddText(current => (normalizeSingleLine(current) === resolvedAddQuery ? '' : current));
  }, [resolvedAddQuery]);
  const [addErrorI18nKey, setAddErrorI18nKey] = useState<string | null>(null);
  const [acceptingId, setAcceptingId] = useState<string | null>(null);
  const [acceptErrorI18nKey, setAcceptErrorI18nKey] = useState<string | null>(null);

  const pending = Boolean(addText.trim() || addingInFlight || removingId || acceptingId);
  useEffect(() => {
    onPendingChange?.(pending);
  }, [onPendingChange, pending]);

  // RE-ARM on every mount, never cleanup-only — React 18 StrictMode's
  // development remount would otherwise leave this stuck at `false` and
  // deaden every later setState.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const handleRemoveKeyQuestion = async (keyQuestion: KeyQuestionDoc) => {
    if (readOnly || busy || removingId !== null) {
      return;
    }
    // The floor holds here too, so reaching this handler through a
    // bypassed `disabled` attribute cannot remove the last remaining key
    // question.
    if (!removalEnabled) {
      return;
    }
    setRemovingId(keyQuestion.id);
    const result = await onRemove(keyQuestion);
    if (!isMountedRef.current) {
      return;
    }
    setRemovingId(null);
    if (result === null) {
      setRemoveErrorI18nKey(null);
      setKeyQuestionsEmptiedByUser(true);
    } else {
      setRemoveErrorI18nKey(result);
    }
  };

  const handleAddKeyQuestion = async () => {
    const sanitized = normalizeSingleLine(addText);
    if (readOnly || busy || !sanitized || addingInFlight) {
      return;
    }
    setAddingInFlight(true);
    setAddErrorI18nKey(null);
    const result = await onAdd(sanitized);
    if (!isMountedRef.current) {
      return;
    }
    setAddingInFlight(false);
    if (result === null) {
      setAddText('');
    } else {
      setAddErrorI18nKey(result);
    }
  };

  const handleAcceptProposal = async (proposalId: string) => {
    if (readOnly || busy || acceptingId !== null) {
      return;
    }
    setAcceptingId(proposalId);
    setAcceptErrorI18nKey(null);
    const result = await onAccept(proposalId);
    if (!isMountedRef.current) {
      return;
    }
    setAcceptingId(null);
    if (result !== null) {
      setAcceptErrorI18nKey(result);
    }
  };

  let body: React.ReactNode;

  if (channelState === 'denied') {
    body = (
      <AdvisoryCalloutState
        tone="info"
        headingKey="advisory:callout.denied.heading"
        bodyKey="advisory:errors.permissionDenied"
      />
    );
  } else if (channelState === 'failed') {
    body = (
      <AdvisoryCalloutState
        tone="failure"
        headingKey="advisory:callout.failed.heading"
        bodyKey={mapAdvisoryErrorCode(channelErrorCode).i18nKey}
        onRetry={onRetryChannel}
      />
    );
  } else if (channelState === 'loading') {
    body = (
      <Box sx={{ textAlign: 'center' }}>
        <Skeleton variant="text" width="60%" sx={[heading]} />
        <Skeleton variant="text" width="40%" sx={[meta]} />
      </Box>
    );
  } else {
    // 'ready' / 'empty' — no card, no remove control and no empty-state
    // copy is reachable in either branch above.
    body = (
      <>
        <Typography
          data-testid="advisory-keyquestions-count"
          sx={[eyebrow, theme => ({ marginTop: theme.visualPrimitives.clHeaderGap })]}
        >
          {t('advisory:keyQuestions.countLabel')}
          {' · '}
          <Box component="span" sx={[mono]}>
            {keyQuestions.length}
          </Box>{' '}
          {t('advisory:keyQuestions.countUnit')}
          {' · '}
          {t('advisory:keyQuestions.countRequirement', { min: KEY_QUESTION_MIN })}
        </Typography>

        {removeErrorI18nKey ? (
          <Typography
            data-testid="advisory-keyquestions-remove-error"
            sx={[meta, theme => ({ marginTop: theme.visualPrimitives.rowGap })]}
          >
            {t(removeErrorI18nKey)}
          </Typography>
        ) : null}

        {/* A non-empty `orphaned` map from the last removal turn — reuses
            this component's own already-established
            heading/meta Typography pattern (identical to the count line
            above and `AdvisoryGoalReviewScreen`'s exhausted-refinements
            notice) rather than a new one-off treatment. Named by question
            TEXT, resolved above, never by raw id. */}
        {orphanedQuestionTexts.length > 0 ? (
          <Box
            data-testid="advisory-keyquestions-orphaned-notice"
            sx={theme => ({ marginTop: theme.visualPrimitives.rowGap })}
          >
            <Typography sx={[heading]}>
              {t('advisory:keyQuestions.orphanedNotice.heading')}
            </Typography>
            <Typography sx={[meta]}>
              {t('advisory:keyQuestions.orphanedNotice.body', {
                questions: orphanedQuestionTexts.join(', '),
              })}
            </Typography>
          </Box>
        ) : null}

        <Box
          data-testid="advisory-keyquestions-list"
          sx={theme => ({ marginTop: theme.visualPrimitives.kqListGap })}
        >
          {orderedKeyQuestions.map((keyQuestion, index) => {
            const questionPosition = index + 1;
            const hasSubquestions = (keyQuestion.subquestions ?? []).length > 0;
            const isExpanded = expandedIds.has(keyQuestion.id);
            return (
              <Box
                key={keyQuestion.id}
                data-testid="advisory-keyquestion-card"
                sx={[keyQuestionCard]}
              >
                <Box sx={[keyQuestionRowGrid]}>
                  <Typography
                    component="span"
                    sx={[
                      mono,
                      theme => ({
                        fontSize: theme.visualPrimitives.kqIndexSize,
                        color: theme.colors.lavenderDeep,
                      }),
                    ]}
                  >
                    {t('advisory:keyQuestions.marker', { position: questionPosition })}
                  </Typography>
                  <Typography
                    component="span"
                    sx={theme => ({
                      minWidth: 0,
                      overflowWrap: 'anywhere',
                      fontSize: theme.visualPrimitives.kqTextSize,
                      fontWeight: theme.visualPrimitives.kqTextWeight,
                      letterSpacing: theme.visualPrimitives.kqTextTracking,
                      lineHeight: theme.visualPrimitives.kqTextLineHeight,
                      color: theme.colors.inkPrimary,
                      cursor: hasSubquestions ? 'pointer' : undefined,
                    })}
                    onClick={
                      hasSubquestions
                        ? () => {
                            setExpandedIds(prev => {
                              const next = new Set(prev);
                              if (next.has(keyQuestion.id)) {
                                next.delete(keyQuestion.id);
                              } else {
                                next.add(keyQuestion.id);
                              }
                              return next;
                            });
                          }
                        : undefined
                    }
                  >
                    {keyQuestion.question}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {hasSubquestions ? (
                      <IconButton
                        size="small"
                        data-testid="advisory-keyquestion-expand"
                        aria-expanded={isExpanded}
                        onClick={() => {
                          setExpandedIds(prev => {
                            const next = new Set(prev);
                            if (next.has(keyQuestion.id)) {
                              next.delete(keyQuestion.id);
                            } else {
                              next.add(keyQuestion.id);
                            }
                            return next;
                          });
                        }}
                        sx={theme => ({
                          color: theme.colors.inkTertiary,
                          padding: '4px',
                          transition: 'transform 200ms ease',
                          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        })}
                      >
                        <ExpandMoreIcon fontSize="small" />
                      </IconButton>
                    ) : null}
                    {!readOnly && (
                      <Button
                        variant="text"
                        disableRipple
                        disabled={busy || removingId !== null || !removalEnabled}
                        onClick={() => handleRemoveKeyQuestion(keyQuestion)}
                        aria-label={t('advisory:keyQuestions.removeKeyQuestionAriaLabel', {
                          position: questionPosition,
                        })}
                        sx={[removeControl, pressable, focusRing]}
                      >
                        {t('advisory:keyQuestions.remove')}
                      </Button>
                    )}
                  </Box>
                </Box>

                <Collapse in={isExpanded} timeout={200}>
                  {(keyQuestion.subquestions ?? []).map((subQuestion, subIndex) => {
                    const subPosition = subIndex + 1;
                    const subPositionLabel = `${questionPosition}.${subPosition}`;
                    return (
                      // eslint-disable-next-line react/no-array-index-key
                      <Box
                        key={subIndex}
                        data-testid="advisory-subquestion-row"
                        sx={[subQuestionRowGrid]}
                      >
                        <Typography
                          component="span"
                          sx={[
                            mono,
                            theme => ({
                              fontSize: theme.visualPrimitives.kqSubIndexSize,
                              color: theme.colors.inkTertiary,
                              textAlign: 'right',
                              paddingRight: theme.visualPrimitives.kqSubIndexGap,
                            }),
                          ]}
                        >
                          {subPositionLabel}
                        </Typography>
                        <Typography
                          component="span"
                          sx={theme => ({
                            minWidth: 0,
                            overflowWrap: 'anywhere',
                            fontSize: theme.visualPrimitives.kqSubTextSize,
                            lineHeight: theme.visualPrimitives.kqSubTextLineHeight,
                            color: theme.colors.inkSecondary,
                          })}
                        >
                          {subQuestion}
                        </Typography>
                      </Box>
                    );
                  })}
                </Collapse>
              </Box>
            );
          })}

          {keyQuestions.length === 0 ? (
            <Typography
              component="div"
              data-testid="advisory-keyquestions-empty"
              sx={theme => ({
                marginTop: theme.visualPrimitives.clEmptyGap,
                fontSize: theme.visualPrimitives.clEmptySize,
                lineHeight: theme.visualPrimitives.clEmptyLineHeight,
                maxWidth: theme.visualPrimitives.clEmptyMaxWidth,
                color: theme.colors.inkTertiary,
              })}
            >
              <Box>
                {t(
                  keyQuestionsEmptiedByUser
                    ? 'advisory:keyQuestions.emptyAfterRemoval.heading'
                    : 'advisory:keyQuestions.empty.heading'
                )}
              </Box>
              <Box>
                {t(
                  keyQuestionsEmptiedByUser
                    ? 'advisory:keyQuestions.emptyAfterRemoval.body'
                    : 'advisory:keyQuestions.empty.body'
                )}
              </Box>
            </Typography>
          ) : null}
        </Box>

        {!readOnly && (
          <Box
            data-testid="advisory-keyquestions-add-field"
            sx={theme => ({
              display: 'flex',
              alignItems: 'center',
              gap: theme.spacing(1),
              marginTop: theme.visualPrimitives.kqListGap,
              borderTop: `1px solid ${theme.visualPrimitives.border}`,
              padding: theme.visualPrimitives.kqCardPadding,
            })}
          >
            <InputBase
              value={addText}
              onChange={e => setAddText(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  handleAddKeyQuestion();
                }
              }}
              placeholder={t('advisory:keyQuestions.addPlaceholder')}
              aria-label={t('advisory:keyQuestions.addAriaLabel')}
              disabled={busy || addingInFlight || removingId !== null || acceptingId !== null}
              inputProps={{ maxLength: 500, 'aria-label': t('advisory:keyQuestions.addAriaLabel') }}
              sx={theme => ({
                flex: 1,
                minWidth: 0,
                fontSize: theme.visualPrimitives.kqTextSize,
                lineHeight: theme.visualPrimitives.kqTextLineHeight,
                color: theme.colors.inkPrimary,
              })}
            />
            <Button
              variant="text"
              disableRipple
              disabled={
                busy ||
                addingInFlight ||
                removingId !== null ||
                acceptingId !== null ||
                !normalizeSingleLine(addText)
              }
              onClick={handleAddKeyQuestion}
              sx={[pillAction, pressable, focusRing, { minWidth: 64 }]}
            >
              {addingInFlight ? (
                <CircularProgress size={16} sx={{ color: 'inherit' }} />
              ) : (
                t('advisory:keyQuestions.addAction')
              )}
            </Button>
          </Box>
        )}

        {addErrorI18nKey ? (
          <Typography
            data-testid="advisory-keyquestions-add-error"
            sx={[meta, theme => ({ marginTop: theme.visualPrimitives.rowGap })]}
          >
            {t(addErrorI18nKey)}
          </Typography>
        ) : null}

        {!readOnly && proposals && proposals.proposals.length > 0 ? (
          <Box
            data-testid="advisory-keyquestions-proposals"
            sx={theme => ({ marginTop: theme.visualPrimitives.kqListGap })}
          >
            <Typography sx={[eyebrow]}>{t('advisory:keyQuestions.proposalHeading')}</Typography>
            {proposals.poorMatch ? (
              <Typography
                data-testid="advisory-keyquestions-poor-match"
                sx={[meta, theme => ({ marginTop: theme.visualPrimitives.rowGap })]}
              >
                {t('advisory:keyQuestions.proposalPoorMatch')}
              </Typography>
            ) : null}
            {proposals.proposals.map(proposal => (
              <Box
                key={proposal.id}
                data-testid="advisory-keyquestion-proposal"
                sx={[keyQuestionCard]}
              >
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: '8px',
                  }}
                >
                  <Box
                    data-testid="advisory-keyquestion-proposal-copy"
                    sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}
                  >
                    <Typography
                      sx={theme => ({
                        fontSize: theme.visualPrimitives.kqTextSize,
                        fontWeight: theme.visualPrimitives.kqTextWeight,
                        lineHeight: theme.visualPrimitives.kqTextLineHeight,
                        color: theme.colors.inkPrimary,
                      })}
                    >
                      {proposal.question}
                    </Typography>
                    <Typography
                      sx={[
                        mono,
                        theme => ({
                          fontSize: theme.visualPrimitives.kqSubIndexSize,
                          color: theme.colors.inkTertiary,
                          marginTop: '4px',
                        }),
                      ]}
                    >
                      {proposal.component}
                    </Typography>
                    <Typography sx={[meta, { marginTop: '4px' }]}>{proposal.reason}</Typography>
                  </Box>
                  <Button
                    variant="text"
                    disableRipple
                    disabled={busy || acceptingId !== null}
                    onClick={() => handleAcceptProposal(proposal.id)}
                    aria-label={t('advisory:keyQuestions.acceptAriaLabel')}
                    sx={[pillAction, pressable, focusRing, { flexShrink: 0 }]}
                  >
                    {t('advisory:keyQuestions.acceptAction')}
                  </Button>
                </Box>
              </Box>
            ))}
            {acceptErrorI18nKey ? (
              <Typography
                data-testid="advisory-keyquestions-accept-error"
                sx={[meta, theme => ({ marginTop: theme.visualPrimitives.rowGap })]}
              >
                {t(acceptErrorI18nKey)}
              </Typography>
            ) : null}
          </Box>
        ) : null}
      </>
    );
  }

  if (embedded) return <Box data-testid="advisory-keyquestions-panel">{body}</Box>;

  return (
    <AdvisoryEngagementShell
      position={position?.position ?? '03'}
      total={position?.total ?? '04'}
      eyebrowKey="advisory:keyQuestions.eyebrow"
      titleKey="advisory:keyQuestions.title"
      hintKey="advisory:keyQuestions.hint"
      actions={
        <>
          <Button variant="text" disableRipple onClick={onBack} sx={[pressable, focusRing]}>
            {t('advisory:keyQuestions.backAction')}
          </Button>
          <Button
            variant="contained"
            disableRipple
            disabled={!confirmEnabled}
            onClick={onConfirm}
            sx={[pillAction, pressable, focusRing]}
          >
            {t('advisory:keyQuestions.confirmAction')}
          </Button>
        </>
      }
    >
      {body}
    </AdvisoryEngagementShell>
  );
}

export default AdvisoryKeyQuestionEditor;
